/**
 * FieldSelector Component
 * Allows selecting/deselecting specific fields for export
 * When applyToAllMode is enabled, changes apply to all selected appointments
 */

import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Check, ChevronDown, ChevronUp, Settings, Users } from "lucide-react";
import { EXPORT_FIELDS } from "../../services/medicalReportService";

const FieldSelector = ({
  selectedFields,
  onFieldChange,
  onCategoryChange,
  onResetDefaults,
  applyToAllMode,
  appointments,
  selectedAppointments,
  onApplyToAllAppointments,
}) => {
  const [expandedCategories, setExpandedCategories] = useState(
    Object.keys(EXPORT_FIELDS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
  );
  const [showTooltip, setShowTooltip] = useState(null);

  const toggleCategory = useCallback((category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  const isCategoryFullySelected = useCallback((category) => {
    const categoryFields = EXPORT_FIELDS[category].fields.map(f => f.key);
    return categoryFields.every(field => selectedFields[category]?.includes(field));
  }, [selectedFields]);

  const isCategoryPartiallySelected = useCallback((category) => {
    const categoryFields = EXPORT_FIELDS[category].fields.map(f => f.key);
    const selectedCount = categoryFields.filter(field => selectedFields[category]?.includes(field)).length;
    return selectedCount > 0 && selectedCount < categoryFields.length;
  }, [selectedFields]);

  const handleCategoryToggle = useCallback((category, checked) => {
    const fields = checked 
      ? EXPORT_FIELDS[category].fields.map(f => f.key)
      : [];
    
    if (applyToAllMode && onApplyToAllAppointments) {
      onApplyToAllAppointments(category, fields);
    } else {
      onCategoryChange(category, checked);
    }
  }, [applyToAllMode, onCategoryChange, onApplyToAllAppointments]);

  const handleFieldToggle = useCallback((category, fieldKey) => {
    const currentFields = selectedFields[category] || [];
    const newFields = currentFields.includes(fieldKey)
      ? currentFields.filter(f => f !== fieldKey)
      : [...currentFields, fieldKey];
    
    if (applyToAllMode && onApplyToAllAppointments) {
      onApplyToAllAppointments(category, newFields);
    } else {
      onFieldChange(category, fieldKey);
    }
  }, [selectedFields, onFieldChange, applyToAllMode, onApplyToAllAppointments]);

  const getFieldSelected = useCallback((category, fieldKey) => {
    return selectedFields[category]?.includes(fieldKey) || false;
  }, [selectedFields]);

  // Count of selected appointments
  const selectedAppointmentCount = Object.values(selectedAppointments || {}).filter(Boolean).length;

  return (
    <div className="field-selector">
      <div className="field-selector__header">
        <h3 className="field-selector__title">
          <Settings size={18} />
          Export Fields
        </h3>
        {applyToAllMode && selectedAppointmentCount > 0 && (
          <div className="field-selector__mode-badge field-selector__mode-badge--all">
            <Users size={12} />
            Applies to {selectedAppointmentCount} appointments
          </div>
        )}
        <button
          type="button"
          className="btn-reset"
          onClick={onResetDefaults}
        >
          Reset Defaults
        </button>
      </div>

      <div className="field-selector__categories">
        {Object.entries(EXPORT_FIELDS).map(([categoryKey, category]) => {
          const isExpanded = expandedCategories[categoryKey];
          const isFullySelected = isCategoryFullySelected(categoryKey);
          const isPartiallySelected = isCategoryPartiallySelected(categoryKey);

          return (
            <div 
              key={categoryKey} 
              className={`field-category ${isExpanded ? "field-category--expanded" : ""}`}
            >
              <div className="field-category__header">
                <label className="field-category__checkbox">
                  <input
                    type="checkbox"
                    checked={isFullySelected}
                    ref={input => {
                      if (input) input.indeterminate = isPartiallySelected && !isFullySelected;
                    }}
                    onChange={(e) => handleCategoryToggle(categoryKey, e.target.checked)}
                  />
                  <span className={`checkbox-mark ${isPartiallySelected && !isFullySelected ? "checkbox-mark--partial" : ""}`}>
                    {isFullySelected && <Check size={12} />}
                    {isPartiallySelected && !isFullySelected && <span className="partial-line" />}
                  </span>
                </label>

                <span 
                  className="field-category__label"
                  onClick={() => toggleCategory(categoryKey)}
                >
                  {category.label}
                  <span className="field-count">
                    ({selectedFields[categoryKey]?.length || 0} / {category.fields.length})
                  </span>
                </span>

                <button
                  type="button"
                  className="field-category__toggle"
                  onClick={() => toggleCategory(categoryKey)}
                >
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {isExpanded && (
                <div className="field-category__fields">
                  {category.fields.map((field) => (
                    <label 
                      key={field.key} 
                      className="field-item"
                      onMouseEnter={() => setShowTooltip(`${categoryKey}-${field.key}`)}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <input
                        type="checkbox"
                        checked={getFieldSelected(categoryKey, field.key)}
                        onChange={() => handleFieldToggle(categoryKey, field.key)}
                      />
                      <span className="checkbox-mark">
                        {getFieldSelected(categoryKey, field.key) && <Check size={10} />}
                      </span>
                      <span className="field-label">{field.label}</span>
                      {field.default && (
                        <span className="default-badge">Default</span>
                      )}
                      {showTooltip === `${categoryKey}-${field.key}` && field.description && (
                        <span className="field-tooltip">{field.description}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

FieldSelector.propTypes = {
  selectedFields: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  onResetDefaults: PropTypes.func.isRequired,
  applyToAllMode: PropTypes.bool,
  appointments: PropTypes.array,
  selectedAppointments: PropTypes.object,
  onApplyToAllAppointments: PropTypes.func,
};

FieldSelector.defaultProps = {
  applyToAllMode: false,
  appointments: [],
  selectedAppointments: {},
  onApplyToAllAppointments: () => {},
};

export default FieldSelector;
